/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, CalendarMonth, Edit } from "@mui/icons-material";
import moment from "moment";
import { useSelector } from "react-redux";
import { tableStyle } from "../../components";
import { CssTextField } from "../../components/TextField";
import Header from "../../components/Header";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import {
  activateMokshaRoomtypeRate,
  addMokshaRoomtypeRate,
  listMokshaRoomtypeRates,
  updateMokshaRoomtypeRate,
} from "../../store/actions";

const startIcon = {
  startAdornment: (
    <InputAdornment position="start">
      <CalendarMonth fontSize="small" sx={{ cursor: "pointer" }} />
    </InputAdornment>
  ),
};

const mokshaRateTypes = [
  { label: "Seasonal", value: "moksha_seasonal" },
  { label: "Weekdays", value: "moksha_weekdays" },
];

// Roomtypes are flexible-occupancy, so the extra-bed rate is what resolution reads.
// `max_occupancy_rate` was the old triple rate and is no longer collected or sent.
const rateFields = ["singleOccupancyRate", "standardRate", "extraBedRate", "childRate"];

// A blank box must never submit as 0: resolution reads a stored 0 literally on some fields
// and falls back to the property's own rate on others, so zero has to be typed.
const isBlankRate = (value) =>
  value === "" || value === null || value === undefined || Number.isNaN(Number(value));

// Blank means "no discount"; anything else has to be a percentage the backend will accept,
// so the range is checked here rather than left to come back as a server error.
const isInvalidDiscount = (raw) => {
  const text = String(raw ?? "").trim();
  if (text === "") return false;
  const value = Number(text);
  return Number.isNaN(value) || value < 0 || value >= 100;
};

// Never defaults to a booking_engine_* type - that default would rewrite a Moksha row
// as everyone's rate.
const emptyRate = () => ({
  startDate: moment().format("YYYY-MM-DD"),
  endDate: moment().add(1, "days").format("YYYY-MM-DD"),
  type: "moksha_seasonal",
  weekdays: [],
  singleOccupancyRate: "",
  standardRate: "",
  extraBedRate: "",
  childRate: "",
  websiteDiscount: "",
  isActive: true,
});

function MokshaRoomtypeRates({ roomtypeId, dispatch, colors }) {
  const startInputRef = useRef();
  const endInputRef = useRef();

  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const isMokshaStaff = loggedInUser?.is_moksha_staff === true;

  const [rates, setRates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [values, setValues] = useState(emptyRate());
  const [error, setError] = useState("");

  const modalStyle = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { sm: 520, xs: "92%" },
      bgcolor: colors.primary[400],
      outline: "none",
      boxShadow: 24,
      p: 2,
      borderRadius: 2,
      maxHeight: "90vh",
      overflowY: "auto",
    }),
    [colors]
  );

  // The flag arrives with the logged in user, which may resolve after mount. Keyed on the
  // roomtype too - the parent swaps it without remounting, and the grid would keep showing
  // the previous roomtype's rates while the form writes to the new one.
  useEffect(() => {
    if (isMokshaStaff) fetchRates();
    // eslint-disable-next-line
  }, [isMokshaStaff, roomtypeId]);

  const fetchRates = async () => {
    const list = await dispatch(listMokshaRoomtypeRates({ roomtype_id: roomtypeId }));
    setRates(list || []);
  };

  const resetForm = () => {
    setEditingId("");
    setValues(emptyRate());
    setError("");
  };

  const openForEdit = (row) => {
    setEditingId(row?._id || "");
    setError("");
    setValues({
      startDate: row?.start_date ? moment.unix(row.start_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      endDate: row?.end_date ? moment.unix(row.end_date).format("YYYY-MM-DD") : moment().add(1, "days").format("YYYY-MM-DD"),
      type: row?.type === "moksha_weekdays" ? "moksha_weekdays" : "moksha_seasonal",
      weekdays: row?.weekdays || [],
      singleOccupancyRate: row?.single_occupancy_rate ?? "",
      standardRate: row?.standard_rate ?? "",
      extraBedRate: row?.extra_bed_rate ?? "",
      childRate: row?.child_rate ?? "",
      websiteDiscount: row?.website_discount_percentage ?? "",
      // Must round-trip: the mutation forwards is_active and the resolver treats anything
      // other than an explicit false as true, so omitting it re-activates the row on save.
      isActive: row?.is_active !== false,
    });
    setOpenModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rateFields.some((field) => isBlankRate(values[field]))) {
      setError("Every rate is required. Type 0 if the rate really is zero.");
      return;
    }
    if (isInvalidDiscount(values.websiteDiscount)) {
      setError("Website discount must be at least 0 and less than 100, or left blank.");
      return;
    }
    setError("");
    setIsSubmitting(true);

    const payload = {
      roomtype_id: roomtypeId,
      type: values.type,
      start_date: moment(values.startDate, "YYYY-MM-DD").unix(),
      end_date: moment(values.endDate, "YYYY-MM-DD").unix(),
      weekdays: values.type === "moksha_weekdays" ? values.weekdays : [],
      standard_rate: Number(values.standardRate || 0),
      single_occupancy_rate: Number(values.singleOccupancyRate || 0),
      extra_bed_rate: Number(values.extraBedRate || 0),
      child_rate: Number(values.childRate || 0),
      // Unlike the rate boxes, blank is meaningful here and means no discount, so this one
      // is not in rateFields and is not required.
      website_discount_percentage: Number(values.websiteDiscount || 0),
      is_active: values.isActive !== false,
    };

    // The thunks resolve to true only when the server accepted the write. Closing
    // unconditionally discarded everything typed whenever a resolver threw - the overlap
    // check, the standard-rate assert and the discount range all reject server-side.
    const saved = editingId
      ? await dispatch(updateMokshaRoomtypeRate({ ...payload, _id: editingId }))
      : await dispatch(addMokshaRoomtypeRate(payload));

    setIsSubmitting(false);

    if (!saved) {
      setError("That rate was not saved. Check the dates and the discount, then try again.");
      return;
    }

    await fetchRates();
    setOpenModal(false);
    resetForm();
  };

  const columns = [
    {
      field: "start_date",
      headerName: "Start Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.start_date ? moment.unix(row.start_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.end_date ? moment.unix(row.end_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "type",
      headerName: "Rate Type",
      width: 130,
      renderCell: ({ row: { type } }) =>
        type === "moksha_weekdays" ? "Weekdays" : "Seasonal",
    },
    {
      field: "weekdays",
      headerName: "Weekdays",
      width: 180,
      renderCell: ({ row: { weekdays } }) =>
        weekdays?.length ? weekdays.join(", ") : "-",
    },
    {
      field: "single_occupancy_rate",
      headerName: "Single",
      width: 90,
      renderCell: ({ row }) => row?.single_occupancy_rate ?? "-",
    },
    {
      field: "standard_rate",
      headerName: "Double",
      width: 90,
      renderCell: ({ row }) => row?.standard_rate ?? "-",
    },
    {
      field: "extra_bed_rate",
      headerName: "Extra bed",
      width: 90,
      renderCell: ({ row }) => row?.extra_bed_rate ?? "-",
    },
    {
      field: "website_discount_percentage",
      headerName: "Website disc.",
      width: 120,
      renderCell: ({ row }) =>
        row?.website_discount_percentage > 0 ? `${row.website_discount_percentage}%` : "-",
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: ({ row: { is_active, _id } }) => (
        <AvailabilityUpdation
          isActive={is_active}
          action={async () => {
            await dispatch(activateMokshaRoomtypeRate({ _id }));
            fetchRates();
          }}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit fontSize="small" />}
          onClick={() => openForEdit(row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (!isMokshaStaff) return null;

  return (
    <>
      <Divider />

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="end" mb={1.5}>
          <Typography variant="h4" fontWeight={700}>
            MOKSHA RATES
          </Typography>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: { sm: "10px 20px", xs: "5px 10px" },
            }}
            onClick={() => setOpenModal(true)}
          >
            <Add sx={{ mr: "10px" }} />
            Add new Moksha rate
          </Button>
        </Stack>
        <Box height="350px" sx={tableStyle(colors)}>
          <DataGrid rows={rates} columns={columns} getRowId={(row) => row?._id} />
        </Box>
      </Box>

      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          resetForm();
        }}
      >
        <Box sx={modalStyle}>
          <Header
            title={editingId ? "UPDATE MOKSHA RATE" : "ADD MOKSHA RATE"}
            titleVariant="h4"
          />
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="Start date"
                name="startDate"
                value={moment(values.startDate).format("YYYY-MM-DD")}
                inputProps={{
                  // No floor when editing: a live rate that started in the past would fail
                  // the browser's range check and block the form with nothing shown.
                  ...(editingId ? {} : { min: moment().format("YYYY-MM-DD") }),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, startDate: e.target.value }))
                }
                inputRef={startInputRef}
                onClick={() => startInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="End date"
                name="endDate"
                value={moment(values.endDate).format("YYYY-MM-DD")}
                inputProps={{
                  min: moment(values.startDate).format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, endDate: e.target.value }))
                }
                inputRef={endInputRef}
                onClick={() => endInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                select
                size="small"
                label="Rate type"
                value={values.type}
                onChange={(e) => setValues((prev) => ({ ...prev, type: e.target.value }))}
              >
                {mokshaRateTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </CssTextField>

              {values.type === "moksha_weekdays" && (
                <CssTextField
                  fullWidth
                  select
                  size="small"
                  label="Weekdays"
                  value={values.weekdays}
                  SelectProps={{ multiple: true }}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, weekdays: e.target.value }))
                  }
                >
                  {moment.weekdays().map((day) => (
                    <MenuItem key={day} value={day}>
                      <Checkbox
                        sx={{ p: "0 5px" }}
                        checked={values.weekdays.indexOf(day) > -1}
                      />
                      {day}
                    </MenuItem>
                  ))}
                </CssTextField>
              )}

              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Single occupancy rate"
                value={values.singleOccupancyRate}
                error={!!error && isBlankRate(values.singleOccupancyRate)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, singleOccupancyRate: e.target.value }))
                }
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Double occupancy rate"
                value={values.standardRate}
                error={!!error && isBlankRate(values.standardRate)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, standardRate: e.target.value }))
                }
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Extra bed rate"
                value={values.extraBedRate}
                error={!!error && isBlankRate(values.extraBedRate)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, extraBedRate: e.target.value }))
                }
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Child rate"
                value={values.childRate}
                error={!!error && isBlankRate(values.childRate)}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, childRate: e.target.value }))
                }
              />
              <CssTextField
                fullWidth
                size="small"
                type="number"
                label="Website discount (%)"
                value={values.websiteDiscount}
                error={!!error && isInvalidDiscount(values.websiteDiscount)}
                inputProps={{ min: 0, max: 99.99, step: "any" }}
                helperText="Taken off the agreed rate on the website. Leave blank for none."
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, websiteDiscount: e.target.value }))
                }
              />
              {error && (
                <Typography variant="body2" color={colors.redAccent[400]}>
                  {error}
                </Typography>
              )}
              <Button fullWidth type="submit" color="secondary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress color="inherit" size={22} />
                ) : (
                  <b>{editingId ? "Update" : "Submit"}</b>
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
    </>
  );
}

export default MokshaRoomtypeRates;
